type AnyError = unknown;

const isHttpRetryableStatus = (status: number): boolean => {
    if (status === 0) return true; // NEW: status 0 => transport failure
    if (status === 408 || status === 429) return true;
    if (status >= 500 && status < 600) return true;
    return false;
};

const NETWORK_CODES = new Set([
    'ETIMEDOUT',
    'ECONNREFUSED',
    'ENOTFOUND',
    'ECONNRESET',
    'EAI_AGAIN',
    'EPIPE',
    'ECONNABORTED',
]);
const NETWORK_KEYWORDS = ['timeout', 'network'];

export const isRetryableError = (error: AnyError): boolean => {
    if (error == null) return false;

    const e: any = error;
    const name: string | undefined = e?.name;
    const code: string | number | undefined = e?.code;
    const response = e?.response;
    const message: string | undefined = e?.message;

    // 1) Caller-initiated cancels
    if (name === 'AbortError' || code === 'ERR_CANCELED') return false;

    // 1a) If the library put an Error INSIDE response, classify that inner error
    if (response instanceof Error) {
        if (response.name === 'AbortError') return false;
        if (response.name === 'TypeError') return true; // fetch/CORS/blocked/offline
        // Fall through; we'll still check for a status below if present
    }

    // 2) HTTP-like numeric status
    const statusViaResponse: number | undefined =
        typeof response?.status === 'number'
            ? response.status
            : typeof response?.status === 'string' &&
              !isNaN(Number(response.status))
            ? Number(response.status)
            : undefined;
    if (typeof statusViaResponse === 'number') {
        return isHttpRetryableStatus(statusViaResponse); // includes status 0
    }

    const statusDirect: number | undefined =
        typeof e?.status === 'number'
            ? e.status
            : typeof e?.status === 'string' && !isNaN(Number(e.status))
            ? Number(e.status)
            : undefined;
    if (typeof statusDirect === 'number') {
        return isHttpRetryableStatus(statusDirect);
    }

    // 3) Transport errors (no HTTP response or unusable response)
    if (typeof message === 'string' && NETWORK_CODES.has(message)) return true;
    if (
        name === 'Error' &&
        typeof message === 'string' &&
        NETWORK_KEYWORDS.some((keyword) =>
            message.toLocaleLowerCase().includes(keyword),
        )
    )
        return true;

    if (
        name === 'Error' &&
        typeof message === 'string' &&
        NETWORK_KEYWORDS.every(
            (keyword) => !message.toLocaleLowerCase().includes(keyword),
        )
    )
        return false;

    if (name === 'TypeError') return true; // fetch threw at top level

    if (e?.isAxiosError && e?.request && !e?.response) return true;

    // If there is a response object but no numeric status, treat as transport-ish
    if (response && typeof response?.status !== 'number') return true;

    // 4) Fallback policy
    if (!response) return true;

    // 5) Has a response we couldn't classify → not retryable
    return false;
};
